(function () {
  'use strict';

  if (window.ImageWithTextSlider && typeof window.ImageWithTextSlider.initAll === 'function') {
    window.ImageWithTextSlider.initAll();
    return;
  }

  class ImageWithTextSlider {
    constructor(root) {
      if (!root || root.dataset.iwtInitialized === 'true') return;

      this.root = root;
      this.viewport = root.querySelector('[data-iwt-viewport]');
      this.slides = Array.from(root.querySelectorAll('[data-iwt-slide]'));
      this.dots = Array.from(root.querySelectorAll('[data-iwt-dot]'));
      this.previousButton = root.querySelector('[data-iwt-previous]');
      this.nextButton = root.querySelector('[data-iwt-next]');
      this.loop = root.dataset.loop === 'true';
      this.pauseOnHover = root.dataset.pauseOnHover === 'true';
      this.autoplayDelay = Number.parseInt(root.dataset.autoplay, 10) || 0;
      this.index = 0;
      this.scrollFrame = null;
      this.autoplayTimer = null;
      this.isEditorPaused = false;
      this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      if (!this.viewport || this.slides.length === 0) return;

      this.onScroll = this.onScroll.bind(this);
      this.onPrevious = this.onPrevious.bind(this);
      this.onNext = this.onNext.bind(this);
      this.onPointerDown = this.stopAutoplay.bind(this);
      this.onPointerUp = this.startAutoplay.bind(this);
      this.onMouseEnter = this.stopAutoplay.bind(this);
      this.onMouseLeave = this.startAutoplay.bind(this);
      this.onFocusIn = this.stopAutoplay.bind(this);
      this.onFocusOut = this.handleFocusOut.bind(this);
      this.onVisibilityChange = this.handleVisibilityChange.bind(this);
      this.onResize = this.handleResize.bind(this);

      this.viewport.addEventListener('scroll', this.onScroll, { passive: true });
      this.viewport.addEventListener('pointerdown', this.onPointerDown, { passive: true });
      this.viewport.addEventListener('pointerup', this.onPointerUp, { passive: true });
      this.viewport.addEventListener('pointercancel', this.onPointerUp, { passive: true });
      this.root.addEventListener('focusin', this.onFocusIn);
      this.root.addEventListener('focusout', this.onFocusOut);

      if (this.pauseOnHover) {
        this.root.addEventListener('mouseenter', this.onMouseEnter);
        this.root.addEventListener('mouseleave', this.onMouseLeave);
      }

      if (this.previousButton) this.previousButton.addEventListener('click', this.onPrevious);
      if (this.nextButton) this.nextButton.addEventListener('click', this.onNext);

      this.dots.forEach((dot, dotIndex) => {
        dot.addEventListener('click', () => this.goTo(dotIndex));
      });

      document.addEventListener('visibilitychange', this.onVisibilityChange);
      window.addEventListener('resize', this.onResize);

      this.root.dataset.iwtInitialized = 'true';
      this.root.iwtSlider = this;
      this.update(0);
      this.startAutoplay();
    }

    onScroll() {
      if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);

      this.scrollFrame = requestAnimationFrame(() => {
        const width = this.viewport.clientWidth;
        if (!width) return;

        const nextIndex = Math.round(this.viewport.scrollLeft / width);
        this.update(nextIndex);
      });
    }

    onPrevious() {
      this.stopAutoplay();
      this.goTo(this.index - 1);
      this.startAutoplay();
    }

    onNext() {
      this.stopAutoplay();
      this.goTo(this.index + 1);
      this.startAutoplay();
    }

    normalizeIndex(index) {
      if (this.loop) {
        if (index < 0) return this.slides.length - 1;
        if (index >= this.slides.length) return 0;
      }

      return Math.max(0, Math.min(index, this.slides.length - 1));
    }

    goTo(index, behavior) {
      const nextIndex = this.normalizeIndex(index);
      const scrollBehavior = behavior || (this.prefersReducedMotion ? 'auto' : 'smooth');

      this.viewport.scrollTo({
        left: nextIndex * this.viewport.clientWidth,
        behavior: scrollBehavior
      });

      this.update(nextIndex);
    }

    update(index) {
      this.index = this.normalizeIndex(index);

      this.slides.forEach((slide, slideIndex) => {
        slide.classList.toggle('is-active', slideIndex === this.index);
      });

      this.dots.forEach((dot, dotIndex) => {
        const isActive = dotIndex === this.index;
        dot.classList.toggle('is-active', isActive);
        dot.setAttribute('aria-current', isActive ? 'true' : 'false');
      });

      if (this.previousButton) {
        this.previousButton.disabled = !this.loop && this.index === 0;
      }

      if (this.nextButton) {
        this.nextButton.disabled = !this.loop && this.index === this.slides.length - 1;
      }
    }

    startAutoplay() {
      this.stopAutoplay();

      if (
        this.autoplayDelay < 1000 ||
        this.slides.length < 2 ||
        this.prefersReducedMotion ||
        this.isEditorPaused ||
        document.hidden
      ) {
        return;
      }

      this.autoplayTimer = window.setInterval(() => {
        this.goTo(this.index + 1);
      }, this.autoplayDelay);
    }

    stopAutoplay() {
      if (this.autoplayTimer) {
        window.clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    }

    handleFocusOut() {
      window.setTimeout(() => {
        if (!this.root.contains(document.activeElement)) this.startAutoplay();
      }, 0);
    }

    handleVisibilityChange() {
      if (document.hidden) {
        this.stopAutoplay();
      } else {
        this.startAutoplay();
      }
    }

    handleResize() {
      this.goTo(this.index, 'auto');
    }

    pauseForEditor() {
      this.isEditorPaused = true;
      this.stopAutoplay();
    }

    resumeFromEditor() {
      this.isEditorPaused = false;
      this.startAutoplay();
    }

    destroy() {
      this.stopAutoplay();

      if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);

      this.viewport.removeEventListener('scroll', this.onScroll);
      this.viewport.removeEventListener('pointerdown', this.onPointerDown);
      this.viewport.removeEventListener('pointerup', this.onPointerUp);
      this.viewport.removeEventListener('pointercancel', this.onPointerUp);
      this.root.removeEventListener('focusin', this.onFocusIn);
      this.root.removeEventListener('focusout', this.onFocusOut);
      this.root.removeEventListener('mouseenter', this.onMouseEnter);
      this.root.removeEventListener('mouseleave', this.onMouseLeave);

      if (this.previousButton) this.previousButton.removeEventListener('click', this.onPrevious);
      if (this.nextButton) this.nextButton.removeEventListener('click', this.onNext);

      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      window.removeEventListener('resize', this.onResize);

      delete this.root.iwtSlider;
      delete this.root.dataset.iwtInitialized;
    }
  }

  const api = {
    initAll(scope) {
      const context = scope || document;
      const roots = [];

      if (context.matches && context.matches('[data-iwt-slider]')) roots.push(context);
      if (context.querySelectorAll) roots.push(...context.querySelectorAll('[data-iwt-slider]'));

      roots.forEach((root) => {
        if (root.dataset.iwtInitialized !== 'true') new ImageWithTextSlider(root);
      });
    }
  };

  window.ImageWithTextSlider = api;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => api.initAll());
  } else {
    api.initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    api.initAll(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-iwt-slider]').forEach((root) => {
      if (root.iwtSlider) root.iwtSlider.destroy();
    });
  });

  document.addEventListener('shopify:block:select', (event) => {
    const selectedSlide = event.target.closest('[data-iwt-slide]');
    if (!selectedSlide) return;

    const root = selectedSlide.closest('[data-iwt-slider]');
    if (!root || !root.iwtSlider) return;

    const index = Number.parseInt(selectedSlide.dataset.slideIndex, 10) || 0;
    root.iwtSlider.pauseForEditor();
    root.iwtSlider.goTo(index, 'auto');
  });

  document.addEventListener('shopify:block:deselect', (event) => {
    const root = event.target.closest('[data-iwt-slider]');
    if (root && root.iwtSlider) root.iwtSlider.resumeFromEditor();
  });
})();
